/**
 * Proximity Preload Strategy
 *
 * Preloads when mouse gets close to a target element.
 * Perfect for button triggers - preload when user hovers near the button.
 */

import type { PreloadStrategy } from './types';
import { createLogger } from '../../shared/logger';

const logger = createLogger('ProximityPreload');

export class ProximityPreloadStrategy implements PreloadStrategy {
	private selector: string;
	private proximityPx: number;
	private hasTriggered = false;
	private boundHandler: (event: MouseEvent) => void;
	private onPreloadCallback: (() => void) | null = null;

	/**
	 * @param selector    CSS selector for the target element(s)
	 * @param proximityPx Distance in pixels to trigger preload (default 100px)
	 */
	constructor(selector: string, proximityPx = 100) {
		logger.log(
			'constructor - selector:',
			selector,
			'proximityPx:',
			proximityPx
		);
		this.selector = selector;
		this.proximityPx = proximityPx;
		this.boundHandler = this.handleMouseMove.bind(this);
	}

	/**
	 * Start listening for mouse proximity
	 */
	public init(onPreload: () => void): void {
		logger.log('init() called');
		this.onPreloadCallback = onPreload;

		// Use passive listener for better scroll performance
		document.addEventListener('mousemove', this.boundHandler, {
			passive: true,
		});
	}

	/**
	 * Stop listening
	 */
	public destroy(): void {
		logger.log('destroy() called');
		document.removeEventListener('mousemove', this.boundHandler);
		this.onPreloadCallback = null;
	}

	/**
	 * Reset the strategy to allow re-preloading
	 * Called when popup is dismissed and we want to preload again on next hover
	 */
	public reset(): void {
		if (!this.hasTriggered) {
			return; // Already reset, nothing to do
		}

		logger.log('reset() called - re-arming proximity detection');
		this.hasTriggered = false;

		// Re-attach listener if we have a callback
		if (this.onPreloadCallback) {
			document.addEventListener('mousemove', this.boundHandler, {
				passive: true,
			});
		}
	}

	/**
	 * Check if mouse is near any target element
	 */
	private handleMouseMove(event: MouseEvent): void {
		if (this.hasTriggered || !this.onPreloadCallback) {
			return;
		}

		const elements = document.querySelectorAll(this.selector);
		if (elements.length === 0) {
			return;
		}

		const mouseX = event.clientX;
		const mouseY = event.clientY;

		for (const element of elements) {
			const rect = element.getBoundingClientRect();

			// Calculate distance from mouse to element edges
			const distanceX = Math.max(
				rect.left - mouseX,
				0,
				mouseX - rect.right
			);
			const distanceY = Math.max(
				rect.top - mouseY,
				0,
				mouseY - rect.bottom
			);
			const distance = Math.sqrt(
				distanceX * distanceX + distanceY * distanceY
			);

			if (distance <= this.proximityPx) {
				logger.log(
					'Mouse within proximity of element! Distance:',
					distance,
					'px'
				);
				this.hasTriggered = true;
				document.removeEventListener('mousemove', this.boundHandler);
				this.onPreloadCallback();
				return;
			}
		}
	}
}
